import { useIsInViewport } from 'hooks/use-is-in-viewport'
import { useIsMounted } from 'hooks/use-is-mounted'
import { Slot } from 'primitives/slot'
import * as React from 'react'
import { tw } from 'utils/classnames'

function Root({
  asChild,
  className,
  children,
}: {
  asChild?: boolean
  className?: string
  children: React.ReactNode
}) {
  const Component = asChild ? Slot : 'div'

  return <Component className={tw('relative', className)}>{children}</Component>
}

function Scroller({ asChild, children }: { asChild?: boolean; children: React.ReactNode }) {
  const Component = asChild ? Slot : 'div'

  return <Component>{children}</Component>
}

function Shadow({
  position,
  className,
  // if you know your Shadow will be visible initially, you can set this to true
  initiallyVisible,
}: {
  position: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  initiallyVisible?: boolean
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const isInViewport = useIsInViewport(ref, !initiallyVisible)
  const isMounted = useIsMounted()

  return (
    <>
      <div ref={ref} />
      <div
        className={tw(
          // sometimes top shadows render too eagerly, so we hide them until they are needed.
          // if you need more control over the top shadow, maybe it's rendering too eagerly still,
          // you can control the component at your level. maybe you should wait until
          // all your list items have loaded before rendering the scroll shadow?
          // i.e. {!isPending && <ScrollShadow position="top" />}
          // so try out that pattern too
          // oxlint-disable-next-line no-nested-ternary
          isMounted
            ? isInViewport
              ? 'opacity-0'
              : 'opacity-100'
            : initiallyVisible
              ? 'opacity-100'
              : 'opacity-0',
          position === 'bottom' && 'bottom-0 left-0 h-8 w-full bg-scrim-to-t-100',
          position === 'top' && 'left-0 top-0 h-8 w-full bg-scrim-to-b-100',
          position === 'left' && 'left-0 top-0 h-full w-8 bg-scrim-to-r-100',
          position === 'right' && 'right-0 top-0 h-full w-8 bg-scrim-to-l-100',
          'text-gray-1000 ease-smooth duration-400 pointer-events-none absolute transition-opacity',
          'z-[1]',
          className,
        )}
      />
    </>
  )
}

/**
 * @deprecated Use `ScrollShadow` from `@repo/studio-design-system-ui-scroll-shadow` instead.
 *
 * The new primitive is a single component driven by Framer Motion's `useScroll`, with a
 * simpler API surface (`direction` + `hideOnStart`) and the wrapper kept separate from
 * the scroll container by construction — which avoids the layout footgun this compound
 * version has (collapsing `Root` onto the scroller via `asChild` makes the shadows land
 * against the bottom of the scroll content box, scrolling with content).
 *
 * Usage of this legacy primitive:
 *
 * <ScrollShadow.Root>
 *  <ScrollShadow.Scroller asChild>
 *   <ul className="scroller-xs"> // or "overflow-y-scroll"
 *    <ScrollShadow.Shadow position="top" />
 *    <li>...</li>
 *    ...
 *    <ScrollShadow.Shadow position="bottom" />
 *   </ul>
 *   </ScrollShadow.Scroller>
 * </ScrollShadow.Root>
 *
 * Note that .Scroller is optional, but it's there to help with DX.
 * Notice how the .Root has the relative className, and the .Scroller has the scroller className.
 */
export const ScrollShadow = {
  /**
   * This component is where the shadow bounding box lives. It renders `relative` className
   * by default, so that the scroll shadows have somewhere to live.
   *
   * Often your scroll area is within the tree of this component.
   */
  Root,
  /**
   * This component is where the scrolling happens. You don't really need this component,
   * it's to help with DX so you don't get lost.
   */
  Scroller,
  Shadow,
}
